import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "../user_styles/main.css";
import UserLogin from "./UserLogin";
import { getDismissedMap, dismissNoticeForToday } from "../utill/notice_storage";
import { getToken, getMe, clearAuth, AUTH_EVENT, getNotices, getPublicObject, sendStt } from "./customerApi";
import { loadZhSettings } from "../utill/mock_zh_settings";
import LanguageSwitcher from "./LanguageSwitcher";
import AccountMenu from "./my/AccountMenu";
import MyPages from "./my/MyPages";
import useBadges from "./my/useBadges";
import { useHashRoute } from "./my/useHashRoute";
import useChatFlow from "./chat/useChatFlow";
import ChatView from "./chat/ChatView";
import { useChatText } from "./chat/text";
import { useLanguage } from "../i18n/LanguageContext";

// 음성 인식 오류 → 안내 문구(그 밖은 서버 오류). 404·409 는 대화가 끝난 것이라 새 대화를 연다.
const STT_ERRORS = { 404: "chat.sessionGone", 409: "chat.maxMessages", 400: "stt.badAudio", 422: "stt.empty", 429: "stt.rateLimited", 504: "stt.timeout" };

// 가맹점주 웹 챗봇(시안 사용자_시안_v1). 대화 흐름은 chat/useChatFlow, 화면은 chat/ChatView.
export default function Main() {
    const { t: tBase, lang } = useLanguage();
    const t = useChatText();

    // 로그인 게이트: 가맹점 토큰(§14-0). 탭을 닫으면 로그아웃(sessionStorage)
    const [customer, setCustomer] = useState(() => (getToken() ? getMe() : null));
    const [loginNotice, setLoginNotice] = useState("");
    const isLoggedIn = !!customer;
    // 내 문의 현황·내 대화 기록 화면(#inq · #log) — 대화는 흐름 훅에 남아 돌아오면 이어진다
    const [route, go] = useHashRoute();
    const [badges, reloadBadges] = useBadges(isLoggedIn, `${route.view}/${route.id}`);
    const chat = useChatFlow({ enabled: isLoggedIn, customer, lang, t });

    const handleLogin = (me) => { setLoginNotice(""); setCustomer(me); };
    const handleLogout = () => { go(""); clearAuth(); setLoginNotice(""); setCustomer(null); chat.reset(); };

    // 토큰이 끊기면(만료·잘못된 토큰) 로그인 화면으로, 이유를 한 줄 보인다
    useEffect(() => {
        const onAuth = () => { go(""); setLoginNotice(tBase("login.expired")); setCustomer(null); chat.reset(); };
        window.addEventListener(AUTH_EVENT, onAuth);
        return () => window.removeEventListener(AUTH_EVENT, onAuth);
    }, [tBase]); // eslint-disable-line react-hooks/exhaustive-deps

    const [messageInput, setMessageInput] = useState("");
    const [welcome, setWelcome] = useState(false);
    const [micStatus, setMicStatus] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false); // 음성 입력 처리 중 (STT 요청~응답)
    const [popupNotices, setPopupNotices] = useState([]);
    const [settings, setSettings] = useState({});
    const endRef = useRef(null);

    // 언어 전환: 이미 그린 대화는 이전 언어라 비운다(세션은 유지)
    const prevLang = useRef(lang);
    useEffect(() => {
        if (prevLang.current === lang) return;
        prevLang.current = lang;
        chat.clear();
        setMessageInput("");
    }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const id = setTimeout(() => setWelcome(true), 1000);
        return () => clearTimeout(id);
    }, []);

    // 환영 문구(/public/settings)·중요 공지 팝업
    useEffect(() => {
        if (!isLoggedIn) return undefined;
        let cancelled = false;
        getPublicObject("/public/settings").then((s) => { if (!cancelled) setSettings(s || {}); });
        getNotices(true).then((list) => {
            if (cancelled) return;
            const map = getDismissedMap();
            const today = new Date().toISOString().split("T")[0];
            setPopupNotices(list.filter((n) => map[n.id] !== today));
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [isLoggedIn]);

    // 중국어(zh) 환영 문구: API *_zh → (과도기) mock → 한국어
    const zhSettings = useMemo(() => loadZhSettings(), [lang]); // eslint-disable-line react-hooks/exhaustive-deps
    const welcomeTitle = (lang === "zh" && (settings.welcome_title_zh || zhSettings.welcome_title_zh)) || settings.welcome_title;
    const welcomeMessage = (lang === "zh" && (settings.welcome_message_zh || zhSettings.welcome_message_zh)) || settings.welcome_message;

    // 새 말·진행 표시·내 화면에서 돌아올 때 대화 끝으로
    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chat.messages, chat.busy, chat.files, isProcessing, route.view]);

    const handleSend = () => {
        const text = messageInput;
        if (!text.trim() || chat.busy) return; // 기다리는 중이면 글을 남겨 둔다
        setMessageInput("");
        chat.send(text);
    };

    // ===== 음성 입력(VAD: 1.5초 무음이면 자동 종료) — 받아쓴 결과는 흐름 훅이 처리 =====
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const STT_LANG = lang === "zh" ? "zh-CN" : "ko-KR";

    const handleSTT = async () => {
        if (!micStatus) {
            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (err) {
                alert(tBase("common.micNotFound"));
                return;
            }
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) audioChunksRef.current.push(event.data);
            };

            mediaRecorder.onstop = async () => {
                const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                setIsProcessing(true);
                const formData = new FormData();
                formData.append("file", blob, "recording.webm");
                formData.append("lang", STT_LANG);
                if (chat.sessionId) formData.append("session_id", chat.sessionId);
                try {
                    const response = await sendStt(formData);
                    if (!response.ok) {
                        const err = new Error(`STT 실패: ${response.status}`);
                        err.status = response.status;
                        throw err;
                    }
                    await chat.voice(await response.json());
                } catch (error) {
                    chat.notice(t(STT_ERRORS[error.status] || "common.serverError"), error.status === 404 || error.status === 409);
                } finally {
                    setIsProcessing(false);
                }
            };

            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(stream);
            const processor = audioContext.createScriptProcessor(2048, 1, 1);
            let silenceStart = null;
            const SILENCE_THRESHOLD = 0.01; // RMS 기준
            const SILENCE_TIMEOUT = 1500; // 1.5초 연속 무음 시 종료

            processor.onaudioprocess = (e) => {
                const input = e.inputBuffer.getChannelData(0);
                const rms = Math.sqrt(input.reduce((sum, v) => sum + v * v, 0) / input.length);
                if (rms < SILENCE_THRESHOLD) {
                    if (!silenceStart) silenceStart = Date.now();
                    else if (Date.now() - silenceStart > SILENCE_TIMEOUT) {
                        // 자동 종료 — stop() 전에 처리 중 상태를 먼저 세워 마이크 버튼이 유휴로 되돌아가는 구간을 없앤다
                        setIsProcessing(true);
                        mediaRecorder.stop();
                        setMicStatus(false);
                        processor.disconnect();
                        source.disconnect();
                    }
                } else {
                    silenceStart = null;
                }
            };
            source.connect(processor);
            processor.connect(audioContext.destination);
            mediaRecorder.start();
        } else {
            // 수동 종료
            setIsProcessing(true);
            mediaRecorderRef.current.stop();
        }
        setMicStatus((prev) => !prev);
    };

    return (
        <>
            <div className="chatbot-service">
                <header className="chatbot-header">
                    <div className="chatbot-header-inner">
                        <i className="chatbot-logo"></i>
                        <div className="chatbot-header-text">
                            <div className="chatbot-header-title">{tBase("header.title")}</div>
                            <div className="chatbot-header-subtitle">{tBase("header.subtitle")}</div>
                        </div>
                    </div>
                    {isLoggedIn && (
                        <div className="chatbot-user-chip">
                            <AccountMenu customer={customer} badges={badges} onGo={go} onLogout={handleLogout} />
                            <button type="button" className="chatbot-logout-button" onClick={handleLogout} title={tBase("login.logout")}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <path d="M16 17l5-5-5-5" />
                                    <path d="M21 12H9" />
                                </svg>
                            </button>
                        </div>
                    )}
                    <LanguageSwitcher />
                </header>

                {!isLoggedIn && <UserLogin onLogin={handleLogin} notice={loginNotice} />}

                {isLoggedIn && (<>
                    <main className="chatbot-chat-area" style={route.view ? { display: "none" } : undefined}>
                        <div className={`loading-section ${welcome ? "fade-out" : "fade-in"}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="none">
                                <path d="M30 0C46.5685 0 60 13.4315 60 30C60 46.5685 46.5685 60 30 60C13.4315 60 0 46.5685 0 30C0 13.4315 13.4315 0 30 0ZM30 13C20.6112 13 13 20.6112 13 30C13 39.3888 20.6112 47 30 47C39.3888 47 47 39.3888 47 30C47 20.6112 39.3888 13 30 13Z" fill="url(#paint0_linear_3312_547)" />
                                <defs>
                                    <linearGradient id="paint0_linear_3312_547" x1="2.66172e-09" y1="16.5517" x2="60.0019" y2="16.5609" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#F3AE2F" />
                                        <stop offset="0.461538" stopColor="#D28A87" />
                                        <stop offset="1" stopColor="#AD61EF" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <h1>{tBase("header.title")}</h1>
                            <p>{tBase("header.tagline")}</p>
                        </div>
                        <ChatView chat={chat} welcomeTitle={welcomeTitle} welcomeMessage={welcomeMessage} go={go} shown={welcome}
                            busyLabel={isProcessing ? tBase("stt.processing") : ""} endRef={endRef} />
                    </main>

                    {route.view && (
                        <MyPages route={route} go={go} customer={customer} onChanged={reloadBadges}
                            onReask={(q) => { go(""); chat.startReask(q); }}
                            onResume={(id) => { go(""); chat.resume(id); }} />
                    )}

                    <footer className="chatbot-input-area" style={route.view ? { display: "none" } : undefined}>
                        <div className="chatbot-input-box">
                            <textarea className="chatbot-input-message" placeholder={tBase("common.inputPlaceholder")}
                                value={messageInput}
                                onChange={(e) => setMessageInput(e.target.value)}
                                onKeyDown={(e) => {
                                    // iOS 등 IME 조합 중 Enter 무시 (한글 마지막 글자가 따로 전송되는 문제 방지)
                                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                            />
                            <div className="chatbot-input-tools">
                                <div className="chatbot-input-tools-left" />
                                <div className="chatbot-input-tools-right">
                                    <button className={`chatbot-input-button mic${micStatus ? "-active" : ""}`}
                                        onClick={() => handleSTT()}
                                        disabled={isProcessing}
                                        aria-label={tBase("common.voiceInput")}
                                    >
                                        <i className="icon-mic-active"></i>
                                    </button>
                                    <button className="chatbot-input-button send" onClick={handleSend} aria-label={tBase("common.send")}>
                                        <i className="icon-send"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </footer>
                </>)}
            </div>

            {popupNotices.length > 0 && (
                <div className="notice-popup-stack">
                    {popupNotices.map((n) => (
                        <div className="notice-popup-card" key={n.id}>
                            <div className="notice-popup-card-header">
                                <h3 className="notice-popup-title">{n.title}</h3>
                            </div>
                            <div className="notice-popup-card-body">
                                <div className="notice-popup-content notice-md">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{n.content}</ReactMarkdown>
                                </div>
                            </div>
                            <div className="notice-popup-card-footer">
                                <button type="button" className="notice-popup-dismiss"
                                    onClick={() => {
                                        dismissNoticeForToday(n.id);
                                        setPopupNotices((prev) => prev.filter((x) => x.id !== n.id));
                                    }}
                                >
                                    {tBase("notice.dismissToday")}
                                </button>
                                <button type="button" className="notice-popup-close" onClick={() => setPopupNotices((prev) => prev.filter((x) => x.id !== n.id))}>
                                    {tBase("common.closeX")}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
